// DrawingCompare.js — ZT Drawing Compare Tab
// Multi-drawing vs one baseline with auto sheet-number pairing

import React, { useState, useRef } from 'react';

const API_BASE = 'https://apispeccheck.zonetap.com';
const GOLD    = '#D4AF37';
const BLACK   = '#000000';
const WHITE   = '#FFFFFF';
const LIGHT_BG = '#fffbeb';
const BORDER  = '#e5e7eb';

let pdfjsReady;
function ensurePdfjs(version = '3.11.174') {
  if (pdfjsReady) return pdfjsReady;
  pdfjsReady = new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
      return resolve(window.pdfjsLib);
    }
    const s = document.createElement('script');
    s.src = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.min.js`;
    s.onload = () => {
      if (!window.pdfjsLib) return reject(new Error('pdfjsLib missing'));
      window.pdfjsLib.GlobalWorkerOptions.workerSrc =
        `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${version}/pdf.worker.min.js`;
      resolve(window.pdfjsLib);
    };
    s.onerror = () => reject(new Error('Failed to load PDF.js'));
    document.head.appendChild(s);
  });
  return pdfjsReady;
}

const generateThumbnail = async (file) => {
  try {
    if (file.type?.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    if (!file.type?.includes('pdf')) return null;
    const lib = await ensurePdfjs();
    const buf = await file.arrayBuffer();
    const pdf = await lib.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    const vp = page.getViewport({ scale: 0.5 });
    const canvas = document.createElement('canvas');
    canvas.width = vp.width;
    canvas.height = vp.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
    return canvas.toDataURL('image/jpeg', 0.8);
  } catch {
    return null;
  }
};

const impactColor = (i) =>
  i === 'High' ? '#dc2626' : i === 'Medium' ? '#d97706' : '#16a34a';

const categoryLabels = {
  spatialChanges:         { label: 'Spatial Changes',            icon: '🏠' },
  structuralChanges:      { label: 'Structural Changes',         icon: '🏗️' },
  doorsWindows:           { label: 'Doors & Windows',            icon: '🚪' },
  mepSystems:             { label: 'MEP / Mechanical',           icon: '⚡' },
  electricalLighting:     { label: 'Electrical & Lighting',      icon: '💡' },
  plumbingFireProtection: { label: 'Plumbing & Fire Protection', icon: '🔧' },
  telecomLowVoltage:      { label: 'Telecom & Low Voltage',      icon: '📡' },
  dimensionsAnnotations:  { label: 'Dimensions & Annotations',   icon: '📐' },
  revisionClouds:         { label: 'Revision Clouds',            icon: '☁️' },
  titleBlock:             { label: 'Title Block',                icon: '📋' },
};

// ── File Preview Modal ────────────────────────────────────────────────────────
const FilePreviewModal = ({ file, thumbnail, isVisible, position, onClose }) => {
  if (!isVisible || !file) return null;
  const w = 400, maxH = 580, pad = 20;
  const left = Math.min(Math.max(position.x + 15, pad), window.innerWidth - w - pad);
  const top  = Math.min(Math.max(position.y - 100, pad), window.innerHeight - maxH - pad);
  const sizeMB   = (file.size / 1024 / 1024).toFixed(2);
  const sizeLabel = file.size > 10*1024*1024 ? 'Large' : file.size > 5*1024*1024 ? 'Medium' : 'Small';
  const sizeColor = file.size > 10*1024*1024 ? { bg: '#fef3c7', text: '#92400e' } : file.size > 5*1024*1024 ? { bg: '#dbeafe', text: '#1e40af' } : { bg: '#dcfce7', text: '#166534' };
  return (
    <div onClick={onClose} style={{ position: 'fixed', left, top, zIndex: 2000, width: w, maxHeight: maxH, background: WHITE, border: `3px solid ${GOLD}`, borderRadius: '16px', padding: '24px', boxShadow: '0 20px 48px rgba(0,0,0,0.4)', overflowY: 'auto', animation: 'ftScale .18s ease-out' }}>
      <style>{`@keyframes ftScale{from{opacity:0;transform:scale(.88)}to{opacity:1;transform:scale(1)}}`}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#111827', margin: 0 }}>📐 Drawing Preview</h3>
        <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
      </div>
      {thumbnail && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px', padding: '12px', background: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
          <img src={thumbnail} alt="preview" style={{ maxWidth: '320px', maxHeight: '460px', objectFit: 'contain', border: '2px solid #d1d5db', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', background: WHITE }} />
        </div>
      )}
      <div style={{ fontSize: '14px', lineHeight: '1.6', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <div style={{ fontWeight: '700', marginBottom: '12px', wordBreak: 'break-word', color: '#111827', fontSize: '15px' }}>📄 {file.name}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px' }}>
          <div><strong>Size:</strong> {sizeMB} MB</div>
          <div><strong>Type:</strong> {file.type || 'Unknown'}</div>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <strong>Last Modified:</strong> {new Date(file.lastModified).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
        </div>
        <div style={{ marginBottom: thumbnail?.includes('data:image') ? '8px' : '0' }}>
          <strong>File Size Category:</strong>{' '}
          <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600', background: sizeColor.bg, color: sizeColor.text }}>{sizeLabel}</span>
        </div>
        {thumbnail?.includes('data:image') && (
          <div style={{ marginTop: '8px', padding: '8px', background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: '6px', color: '#065f46', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span>✅</span><span><strong>PDF Preview Generated</strong> — First page rendered successfully</span>
          </div>
        )}
      </div>
      <div style={{ marginTop: '14px', fontSize: '12px', color: '#6b7280', textAlign: 'center', fontStyle: 'italic' }}>Click anywhere to close</div>
    </div>
  );
};

// ── Single-file Drop Zone ─────────────────────────────────────────────────────
const DrawingDropZone = ({ label, file, thumbnail, onFileDrop, onClear }) => {
  const ref = useRef();
  const [drag, setDrag] = React.useState(false);
  const [hover, setHover] = React.useState(false);
  const [mpos, setMpos] = React.useState({ x: 0, y: 0 });
  const onDrop = (e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer?.files?.[0] || e.target?.files?.[0]; if (f) onFileDrop(f); };
  return (
    <>
      <div style={{ border: `2px dashed ${drag || file ? GOLD : '#d1d5db'}`, borderRadius: '10px', background: file ? LIGHT_BG : drag ? LIGHT_BG : '#f9fafb', minHeight: '130px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '14px', cursor: file ? 'default' : 'pointer', transition: 'all .2s' }}
        onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={onDrop} onClick={() => !file && ref.current?.click()}>
        <input ref={ref} type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={onDrop} />
        {file ? (
          <>
            <div style={{ position: 'relative', cursor: 'pointer', marginBottom: '6px' }} onMouseEnter={e => { setMpos({ x: e.clientX, y: e.clientY }); setHover(true); }} onMouseLeave={() => setHover(false)}>
              {thumbnail ? <img src={thumbnail} alt="" style={{ width: '58px', height: '74px', objectFit: 'cover', borderRadius: '5px', border: `2px solid ${hover ? GOLD : '#d1d5db'}`, transition: 'all .2s', transform: hover ? 'scale(1.06)' : 'scale(1)' }} />
                : <div style={{ fontSize: '36px' }}>📄</div>}
              {thumbnail?.includes('data:image') && <div style={{ position: 'absolute', top: '-5px', right: '-5px', background: '#22c55e', color: WHITE, borderRadius: '50%', width: '16px', height: '16px', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700' }}>✓</div>}
            </div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#111', wordBreak: 'break-word', maxWidth: '150px', textAlign: 'center', marginBottom: '6px' }}>{file.name.length > 22 ? file.name.slice(0, 19) + '…' : file.name}</div>
            <button onClick={e => { e.stopPropagation(); setHover(false); onClear(); }} style={{ background: '#dc2626', color: WHITE, border: 'none', borderRadius: '5px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer' }}>Remove</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: '28px', marginBottom: '5px' }}>📐</div>
            <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '3px', textAlign: 'center' }}>{label}</div>
            <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>PDF or image — drag & drop or click</div>
          </>
        )}
      </div>
      <FilePreviewModal file={file} thumbnail={thumbnail} isVisible={hover} position={mpos} onClose={() => setHover(false)} />
    </>
  );
};

// ── Thumbnail Grid with hover preview ────────────────────────────────────────
const ThumbGrid = ({ drawings, onRemove }) => {
  const [hoverIdx, setHoverIdx] = React.useState(null);
  const [mpos, setMpos]         = React.useState({ x: 0, y: 0 });

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '8px', marginTop: '12px' }}>
      {drawings.map((d, i) => (
        <div key={i} style={{ position: 'relative', background: '#f9fafb', borderRadius: '8px', border: `2px solid ${hoverIdx === i && d.thumb ? GOLD : BORDER}`, padding: '6px', textAlign: 'center', transition: 'border-color .15s' }}>
          <button
            onClick={() => onRemove(i)}
            style={{ position: 'absolute', top: '3px', right: '3px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '50%', width: '15px', height: '15px', fontSize: '9px', cursor: 'pointer', lineHeight: '15px', padding: 0, zIndex: 1 }}
          >×</button>

          {/* Hoverable thumbnail */}
          <div
            style={{ cursor: d.thumb ? 'pointer' : 'default' }}
            onMouseEnter={e => { setMpos({ x: e.clientX, y: e.clientY }); setHoverIdx(i); }}
            onMouseLeave={() => setHoverIdx(null)}
          >
            {d.thumb
              ? <img src={d.thumb} alt="" style={{ width: '52px', height: '66px', objectFit: 'cover', borderRadius: '3px', border: `1px solid ${BORDER}`, transform: hoverIdx === i ? 'scale(1.06)' : 'scale(1)', transition: 'transform .15s' }} />
              : <div style={{ fontSize: '26px', lineHeight: 1, marginBottom: '4px' }}>📄</div>
            }
          </div>

          <div style={{ fontSize: '9px', marginTop: '3px', wordBreak: 'break-word', fontWeight: '600', color: '#374151', lineHeight: '1.3' }}>
            {d.file.name.length > 14 ? d.file.name.slice(0, 11) + '…' : d.file.name}
          </div>
          {d.sheetNum && (
            <div style={{ display: 'inline-block', padding: '1px 6px', borderRadius: '20px', fontSize: '9px', fontWeight: '600', background: GOLD + '22', color: GOLD, border: `1px solid ${GOLD}44`, marginTop: '2px' }}>
              {d.sheetNum}
            </div>
          )}

          {/* Hover preview */}
          <FilePreviewModal
            file={d.file}
            thumbnail={d.thumb}
            isVisible={hoverIdx === i}
            position={mpos}
            onClose={() => setHoverIdx(null)}
          />
        </div>
      ))}
    </div>
  );
};

// ── Branded Confirm Modal ─────────────────────────────────────────────────────
const ConfirmModal = ({ isOpen, title, message, confirmLabel = 'Remove', onConfirm, onCancel }) => {
  if (!isOpen) return null;
  return (
    <div onClick={onCancel} style={{ position: 'fixed', inset: 0, zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,.45)', backdropFilter: 'blur(2px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: WHITE, borderRadius: '16px', border: `2px solid ${GOLD}`, padding: '30px 26px', width: '100%', maxWidth: '390px', boxShadow: '0 24px 60px rgba(0,0,0,.28)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
          <div style={{ background: '#fef2f2', borderRadius: '50%', width: '42px', height: '42px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', flexShrink: 0 }}>🗑️</div>
          <div style={{ fontSize: '17px', fontWeight: '700' }}>{title}</div>
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280', marginBottom: '22px', lineHeight: '1.6', paddingLeft: '54px' }}>{message}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '18px', paddingLeft: '54px' }}>
          <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: GOLD }} />
          <span style={{ fontSize: '10px', fontWeight: '700', color: GOLD, letterSpacing: '.08em' }}>AECWorks AI</span>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
          <button onClick={onCancel} style={{ background: 'transparent', border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '8px 18px', fontSize: '13px', color: '#6b7280', cursor: 'pointer' }}>Cancel</button>
          <button onClick={onConfirm} style={{ background: '#dc2626', border: 'none', borderRadius: '8px', padding: '8px 18px', fontSize: '13px', color: WHITE, cursor: 'pointer', fontWeight: '700' }}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function DrawingCompare({ user, externalNewFile, externalBaselineFile, onExternalNewFileConsumed, onExternalBaselineFileConsumed }) {
  const userId = user?.email || user?.sub || 'anonymous';

  // newDrawings:      [{ file, thumb, sheetNum }]
  // baselineDrawings: [{ file, thumb, sheetNum }] — uploaded set OR single
  const [newDrawings, setNewDrawings]           = useState([]);
  const [baselineDrawings, setBaselineDrawings] = useState([]);
  const [baselineMode, setBaselineMode]         = useState('upload'); // 'upload' | 'vault'
  const [selectedVaultIds, setSelectedVaultIds] = useState([]); // multi-vault selection
  const [projectName, setProjectName]           = useState('');
  const [saveAsBaseline, setSaveAsBaseline]     = useState(false);
  const [pairingOpen, setPairingOpen]           = useState(null);
  const [vault, setVault]                 = useState([]);
  const [vaultLoading, setVaultLoading]   = useState(false);
  const [vaultLoaded, setVaultLoaded]     = useState(false);
  const [confirmModal, setConfirmModal]   = useState({ isOpen: false, vaultId: null });
  const [analyzing, setAnalyzing]         = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ current: 0, total: 0 });
  const [results, setResults]             = useState([]);
  const [activeResultIdx, setActiveResultIdx] = useState(0);
  const [error, setError]                 = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [customFocus, setCustomFocus] = useState('');

  const newRef      = useRef();
  const baselineRef = useRef();

  // ── Helpers ───────────────────────────────────────────────────────────────
  const extractSheetNum = (filename) => {
    const m = filename.match(/[A-Z]{1,2}[-.]?\d{1,3}(\.\d)?/i);
    return m ? m[0].toUpperCase() : '';
  };

  // ── File handlers ─────────────────────────────────────────────────────────
  const makeDrawingEntry = async (file) => {
    const thumb    = await generateThumbnail(file);
    const sheetNum = extractSheetNum(file.name);
    return { file, thumb, sheetNum };
  };

  const handleNewFiles      = async (files) => { const entries = await Promise.all(files.map(makeDrawingEntry)); setNewDrawings(prev => [...prev, ...entries]); };
  const handleBaselineFiles = async (files) => { const entries = await Promise.all(files.map(makeDrawingEntry)); setBaselineDrawings(prev => [...prev, ...entries]); };

  const removeNewDrawing      = (idx) => setNewDrawings(prev => prev.filter((_, i) => i !== idx));
  const removeBaselineDrawing = (idx) => setBaselineDrawings(prev => prev.filter((_, i) => i !== idx));

  // Auto-pair: for each new drawing, find matching baseline by sheet number
  const getAutoMatch = (newD) => {
    if (!newD.sheetNum) return null;
    return baselineDrawings.find(b => b.sheetNum === newD.sheetNum) || null;
  };

  // Vault toggle
  const toggleVaultId = (id) => setSelectedVaultIds(prev => prev.includes(id) ? prev.filter(v => v !== id) : [...prev, id]);

  // External file loading (from Recent Docs / Platform Files panels)
  const handleNewFile      = async (file) => handleNewFiles([file]);
  const handleBaselineFile = async (file) => handleBaselineFiles([file]);

  React.useEffect(() => { if (externalNewFile)      { handleNewFile(externalNewFile);       onExternalNewFileConsumed?.();      } }, [externalNewFile]);
  React.useEffect(() => { if (externalBaselineFile) { handleBaselineFile(externalBaselineFile); onExternalBaselineFileConsumed?.(); } }, [externalBaselineFile]);

  // ── Vault ─────────────────────────────────────────────────────────────────
  const loadVault = async () => {
    if (vaultLoaded) return;
    setVaultLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/drawing-compare/vault/list?userId=${encodeURIComponent(userId)}`);
      const data = await res.json();
      if (data.success) { setVault(data.drawings); setVaultLoaded(true); }
    } catch (e) { console.error('Vault load error:', e); }
    finally { setVaultLoading(false); }
  };

  const deleteFromVault      = (id) => setConfirmModal({ isOpen: true, vaultId: id });
  const confirmDeleteFromVault = async () => {
    const id = confirmModal.vaultId;
    setConfirmModal({ isOpen: false, vaultId: null });
    try {
    await fetch(`${API_BASE}/api/drawing-compare/vault/${id}?userId=${encodeURIComponent(userId)}`, { method: 'DELETE' });
      setVault(prev => prev.filter(d => d.id !== id));
      setSelectedVaultIds(prev => prev.filter(v => v !== id));
    } catch (e) { console.error('Delete vault error:', e); }
  };

  // ── Resolve baseline for a new drawing ──────────────────────────────────
  // Priority: auto-matched by sheet# → first uploaded baseline → first vault selection
  const resolveBaseline = (newD) => {
    if (baselineMode === 'vault') {
      const matchedVault = vault.find(v => newD.sheetNum && extractSheetNum(v.fileName) === newD.sheetNum && selectedVaultIds.includes(v.id));
      return { vaultId: matchedVault?.id || selectedVaultIds[0] || null, file: null };
    } else {
      const matched = getAutoMatch(newD);
      return { vaultId: null, file: matched?.file || baselineDrawings[0]?.file || null, name: matched?.file?.name || baselineDrawings[0]?.file?.name };
    }
  };

  // ── Analyze ───────────────────────────────────────────────────────────────
  const handleAnalyze = async () => {
    if (newDrawings.length === 0) { setError('Please upload at least one new drawing.'); return; }
    if (baselineMode === 'upload' && baselineDrawings.length === 0) { setError('Please upload at least one baseline drawing.'); return; }
    if (baselineMode === 'vault'  && selectedVaultIds.length === 0)  { setError('Please select at least one baseline from the vault.'); return; }

    // Validate each drawing has a resolvable baseline
    for (const d of newDrawings) {
      const b = resolveBaseline(d);
      if (!b.vaultId && !b.file) { setError(`No baseline found for "${d.file.name}". Upload a matching baseline or select one from the vault.`); return; }
    }

    setAnalyzing(true); setError(null); setResults([]); setActiveResultIdx(0);
    setAnalysisProgress({ current: 0, total: newDrawings.length });

    const allResults = [];
    for (let i = 0; i < newDrawings.length; i++) {
      const d = newDrawings[i];
      const b = resolveBaseline(d);
      setAnalysisProgress({ current: i + 1, total: newDrawings.length });
      try {
        const fd = new FormData();
        fd.append('userId', userId);
        fd.append('projectName', projectName);
        fd.append('sheetNumber', d.sheetNum || '');
        fd.append('saveAsBaseline', saveAsBaseline ? 'true' : 'false');
        fd.append('baselineLabel', d.sheetNum || '');
        if (customFocus.trim()) fd.append('customFocus', customFocus.trim());
        fd.append('newDrawing', d.file);
        if (b.vaultId) fd.append('baselineId',      b.vaultId);
        else           fd.append('baselineDrawing', b.file);
        const res  = await fetch(`${API_BASE}/api/drawing-compare/analyze`, { method: 'POST', body: fd });
        const data = await res.json();
        allResults.push({ success: data.success, analysis: data.analysis, newDrawingName: data.newDrawingName, baselineDrawingName: data.baselineDrawingName, sheetNum: d.sheetNum, error: data.success ? null : (data.error || 'Analysis failed') });
      } catch (e) {
        allResults.push({ success: false, error: e.message, newDrawingName: d.file.name, sheetNum: d.sheetNum });
      }
    }
    setResults(allResults);
    if (saveAsBaseline) setVaultLoaded(false);
    setAnalyzing(false);
  };

  // ── Export PDF (active sheet) ─────────────────────────────────────────────
  const exportPDF = async () => {
    const res = results[activeResultIdx];
    if (!res?.analysis) return;
    try {
      const r = await fetch(`${API_BASE}/api/drawing-compare/export-pdf`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ analysis: res.analysis, newDrawingName: res.newDrawingName, baselineDrawingName: res.baselineDrawingName, projectName, sheetNumber: res.sheetNum })
      });
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url; a.download = `ZT-Drawing-Compare-${res.sheetNum || Date.now()}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('PDF export error:', e); }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────
  const resetForm = () => {
    setNewDrawings([]); setBaselineDrawings([]);
    setSelectedVaultIds([]); setResults([]); setActiveResultIdx(0);
    setError(null); setSaveAsBaseline(false); setPairingOpen(null); setExpandedCategories({}); setCustomFocus('');
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const S = {
    section:        { background: WHITE, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '18px 20px', marginBottom: '14px' },
    sectionTitle:   { fontSize: '14px', fontWeight: '700', color: BLACK, marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' },
    btnGold:        { background: GOLD, color: BLACK, border: 'none', borderRadius: '8px', padding: '10px 22px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' },
    btnGhost:       { background: 'transparent', color: '#6b7280', border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '6px 13px', fontSize: '12px', cursor: 'pointer' },
    input:          { width: '100%', padding: '8px 11px', border: `1px solid ${BORDER}`, borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box' },
    label:          { fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '4px', display: 'block' },
    pill:           (c) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: c + '22', color: c, border: `1px solid ${c}44` }),
    vaultCard:      (sel) => ({ border: `2px solid ${sel ? GOLD : BORDER}`, borderRadius: '8px', padding: '9px 13px', cursor: 'pointer', background: sel ? LIGHT_BG : WHITE, marginBottom: '5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'all .15s' }),
    catHeader:      (exp) => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 13px', background: exp ? LIGHT_BG : '#f9fafb', border: `1px solid ${exp ? GOLD : BORDER}`, borderRadius: exp ? '8px 8px 0 0' : '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' }),
    changeItem:     { padding: '8px 13px', borderBottom: `1px solid ${BORDER}`, fontSize: '13px', display: 'flex', gap: '10px', alignItems: 'flex-start' },
  };

  // ── Render single sheet result ────────────────────────────────────────────
  const renderSheetResult = (res, idx) => {
    if (!res) return null;
    if (!res.success) return (
      <div style={{ ...S.section, background: '#fef2f2', border: '1px solid #fca5a5' }}>
        <div style={{ fontWeight: '700', color: '#dc2626', marginBottom: '5px' }}>Analysis failed — {res.newDrawingName}</div>
        <div style={{ fontSize: '13px', color: '#6b7280' }}>{res.error}</div>
      </div>
    );
    if (res.analysis?.parseError) return (
      <div style={S.section}>
        <div style={S.sectionTitle}>Raw Result — {res.newDrawingName}</div>
        <pre style={{ fontSize: '12px', whiteSpace: 'pre-wrap' }}>{res.analysis.raw}</pre>
      </div>
    );

    const { overallAssessment, categories, recommendations } = res.analysis;
    const revColor = overallAssessment?.revisionLevel === 'Major' ? '#dc2626' : overallAssessment?.revisionLevel === 'Moderate' ? '#d97706' : '#16a34a';
    const ck = (key) => `${idx}-${key}`;

    return (
      <div>
        <div style={{ ...S.section, background: LIGHT_BG, border: `2px solid ${GOLD}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <div style={{ fontSize: '15px', fontWeight: '700' }}>Drawing Comparison Report</div>
            <span style={S.pill(revColor)}>{overallAssessment?.revisionLevel} Revision</span>
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
            <strong>New:</strong> {res.newDrawingName} &nbsp;&rarr;&nbsp; <strong>Baseline:</strong> {res.baselineDrawingName}
          </div>
          <div style={{ fontSize: '13px', color: '#374151', marginBottom: '12px', lineHeight: '1.6' }}>{overallAssessment?.summary}</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {[[`${overallAssessment?.totalChangesFound || 0}`, 'Total Changes', BLACK], [`${overallAssessment?.highImpactChanges || 0}`, 'High Impact', '#dc2626']].map(([val, lbl, clr]) => (
              <div key={lbl} style={{ background: WHITE, borderRadius: '8px', padding: '8px 14px', textAlign: 'center' }}>
                <div style={{ fontSize: '22px', fontWeight: '700', color: clr }}>{val}</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>{lbl}</div>
              </div>
            ))}
          </div>
        </div>

        {Object.entries(categoryLabels).map(([key, { label, icon }]) => {
          const cat = categories?.[key];
          if (!cat || cat.count === 0) return null;
          const exp = expandedCategories[ck(key)];
          return (
            <div key={key} style={{ marginBottom: '6px' }}>
              <div style={S.catHeader(exp)} onClick={() => setExpandedCategories(p => ({ ...p, [ck(key)]: !p[ck(key)] }))}>
                <span>{icon} {label} <span style={S.pill(GOLD)}>{cat.count}</span></span>
                <span style={{ color: '#9ca3af' }}>{exp ? '▲' : '▼'}</span>
              </div>
              {exp && (
                <div style={{ border: `1px solid ${GOLD}`, borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                  {cat.changes.map((ch, i) => (
                    <div key={i} style={{ ...S.changeItem, background: i % 2 === 0 ? WHITE : '#fafafa' }}>
                      <span style={S.pill(impactColor(ch.impact))}>{ch.impact}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', marginBottom: '2px' }}>{ch.description}</div>
                        {ch.location && <div style={{ color: '#6b7280', fontSize: '12px' }}>📍 {ch.location}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {recommendations?.length > 0 && (
          <div style={{ ...S.section, marginTop: '10px' }}>
            <div style={S.sectionTitle}>💡 Recommended Actions</div>
            {recommendations.map((rec, i) => (
              <div key={i} style={{ display: 'flex', gap: '10px', padding: '7px 0', borderBottom: i < recommendations.length - 1 ? `1px solid ${BORDER}` : 'none', fontSize: '13px' }}>
                <span style={{ color: GOLD, fontWeight: '700' }}>{i + 1}.</span><span>{rec}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  // ── Results view ──────────────────────────────────────────────────────────
  const renderResults = () => {
    const totalChanges = results.reduce((s, r) => s + (r.analysis?.overallAssessment?.totalChangesFound || 0), 0);
    const highImpact   = results.reduce((s, r) => s + (r.analysis?.overallAssessment?.highImpactChanges  || 0), 0);
    return (
      <div>
        {/* Multi-sheet summary bar */}
        {results.length > 1 && (
          <div style={{ ...S.section, background: LIGHT_BG, border: `2px solid ${GOLD}`, marginBottom: '14px' }}>
            <div style={{ fontSize: '15px', fontWeight: '700', marginBottom: '12px' }}>
              Set Comparison Summary — {results.length} Sheets
            </div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
              {[['📄', results.length, 'Sheets', BLACK], ['🔍', totalChanges, 'Total Changes', GOLD], ['⚠️', highImpact, 'High Impact', '#dc2626']].map(([icon, val, lbl, clr]) => (
                <div key={lbl} style={{ background: WHITE, borderRadius: '8px', padding: '8px 14px', textAlign: 'center', minWidth: '76px' }}>
                  <div style={{ fontSize: '16px', marginBottom: '2px' }}>{icon}</div>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: clr }}>{val}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>{lbl}</div>
                </div>
              ))}
            </div>
            {/* Sheet tabs */}
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {results.map((r, i) => {
                const rc = r.analysis?.overallAssessment?.revisionLevel === 'Major' ? '#dc2626' : r.analysis?.overallAssessment?.revisionLevel === 'Moderate' ? '#d97706' : '#16a34a';
                return (
                  <button key={i} onClick={() => setActiveResultIdx(i)} style={{ padding: '5px 12px', borderRadius: '20px', border: `2px solid ${i === activeResultIdx ? GOLD : BORDER}`, background: i === activeResultIdx ? LIGHT_BG : WHITE, fontWeight: i === activeResultIdx ? '700' : '400', fontSize: '12px', cursor: 'pointer', color: BLACK, display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: r.success ? rc : '#9ca3af', display: 'inline-block' }} />
                    {r.sheetNum || `Sheet ${i + 1}`}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {renderSheetResult(results[activeResultIdx], activeResultIdx)}

        <div style={{ background: '#f9fafb', border: `1px solid ${BORDER}`, borderRadius: '8px', padding: '10px 14px', margin: '14px 0', fontSize: '11px', color: '#9ca3af' }}>
          Generated by ZT AI Tools for informational purposes only. All findings should be verified by a qualified professional before any action is taken.
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button style={S.btnGold} onClick={exportPDF}>⬇ Download PDF Report</button>
          <button style={S.btnGhost} onClick={resetForm}>← New Comparison</button>
        </div>
      </div>
    );
  };

  // ── Main form ─────────────────────────────────────────────────────────────
  if (results.length > 0) return (
    <>
      {renderResults()}
      <ConfirmModal isOpen={confirmModal.isOpen} title="Remove from Vault" message="This baseline drawing will be permanently removed from your Drawing Vault. This action cannot be undone." confirmLabel="Yes, Remove" onConfirm={confirmDeleteFromVault} onCancel={() => setConfirmModal({ isOpen: false, vaultId: null })} />
    </>
  );

  return (
    <>
      <ConfirmModal isOpen={confirmModal.isOpen} title="Remove from Vault" message="This baseline drawing will be permanently removed from your Drawing Vault. This action cannot be undone." confirmLabel="Yes, Remove" onConfirm={confirmDeleteFromVault} onCancel={() => setConfirmModal({ isOpen: false, vaultId: null })} />
      <div>

        {/* ── Two-panel upload row ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '16px' }}>

          {/* LEFT: New / Revised Drawings */}
          <div style={S.section}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={S.sectionTitle}>
                📄 New / Current Drawing{newDrawings.length !== 1 ? 's' : ''}
                {newDrawings.length > 0 && <span style={S.pill(GOLD)}>{newDrawings.length}</span>}
              </div>
              {newDrawings.length > 0 && <button style={{ ...S.btnGhost, color: '#dc2626', fontSize: '11px' }} onClick={() => setNewDrawings([])}>Clear All</button>}
            </div>
            <div
              style={{ border: `2px dashed ${GOLD}`, borderRadius: '10px', padding: '14px', textAlign: 'center', cursor: 'pointer', background: LIGHT_BG, minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
              onDragOver={e => e.preventDefault()}
              onDrop={e => { e.preventDefault(); handleNewFiles([...e.dataTransfer.files]); }}
              onClick={() => newRef.current?.click()}
            >
              <input ref={newRef} type="file" accept=".pdf,image/*" multiple style={{ display: 'none' }} onChange={e => handleNewFiles([...e.target.files])} />
              <div style={{ fontSize: '24px', marginBottom: '5px' }}>📐</div>
              <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>{newDrawings.length === 0 ? 'Upload New Drawing(s)' : '+ Add More'}</div>
              <div style={{ fontSize: '11px', color: '#9ca3af' }}>PDF or image — single or multiple sheets</div>
            </div>
            {newDrawings.length > 0 && <ThumbGrid drawings={newDrawings} onRemove={removeNewDrawing} />}
          </div>

          {/* RIGHT: Baseline / Previous Drawings */}
          <div style={S.section}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div style={S.sectionTitle}>
                📁 Baseline / Previous Drawing{baselineDrawings.length !== 1 ? 's' : ''}
                {baselineMode === 'upload' && baselineDrawings.length > 0 && <span style={S.pill('#6b7280')}>{baselineDrawings.length}</span>}
                {baselineMode === 'vault'  && selectedVaultIds.length  > 0 && <span style={S.pill('#6b7280')}>{selectedVaultIds.length}</span>}
              </div>
              {baselineMode === 'upload' && baselineDrawings.length > 0 && <button style={{ ...S.btnGhost, color: '#dc2626', fontSize: '11px' }} onClick={() => setBaselineDrawings([])}>Clear All</button>}
            </div>

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: '7px', marginBottom: '12px' }}>
              <button style={{ ...S.btnGhost, ...(baselineMode === 'upload' ? { background: GOLD, color: BLACK, border: 'none', fontWeight: '700' } : {}) }} onClick={() => setBaselineMode('upload')}>Upload File(s)</button>
              <button style={{ ...S.btnGhost, ...(baselineMode === 'vault'  ? { background: GOLD, color: BLACK, border: 'none', fontWeight: '700' } : {}) }} onClick={() => { setBaselineMode('vault'); loadVault(); }}>🗄 Drawing Vault</button>
            </div>

            {baselineMode === 'upload' ? (
              <>
                <div
                  style={{ border: `2px dashed ${GOLD}`, borderRadius: '10px', padding: '14px', textAlign: 'center', cursor: 'pointer', background: LIGHT_BG, minHeight: '120px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                  onDragOver={e => e.preventDefault()}
                  onDrop={e => { e.preventDefault(); handleBaselineFiles([...e.dataTransfer.files]); }}
                  onClick={() => baselineRef.current?.click()}
                >
                  <input ref={baselineRef} type="file" accept=".pdf,image/*" multiple style={{ display: 'none' }} onChange={e => handleBaselineFiles([...e.target.files])} />
                  <div style={{ fontSize: '24px', marginBottom: '5px' }}>📁</div>
                  <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '2px' }}>{baselineDrawings.length === 0 ? 'Upload Baseline Drawing(s)' : '+ Add More'}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>PDF or image — single or multiple sheets</div>
                </div>
                {baselineDrawings.length > 0 && <ThumbGrid drawings={baselineDrawings} onRemove={removeBaselineDrawing} />}
              </>
            ) : (
              <div>
                {vaultLoading ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>Loading vault...</div>
                ) : vault.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280', background: '#f9fafb', borderRadius: '8px', fontSize: '13px' }}>
                    No saved baselines yet.<br /><span style={{ fontSize: '11px' }}>Save a drawing as baseline after your first comparison.</span>
                  </div>
                ) : (
                  <div style={{ maxHeight: '240px', overflowY: 'auto' }}>
                    {vault.map(v => {
                      const sel = selectedVaultIds.includes(v.id);
                      return (
                        <div key={v.id} style={S.vaultCard(sel)} onClick={() => toggleVaultId(v.id)}>
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '12px' }}>{v.fileName}</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>
                              {v.projectName && `${v.projectName} · `}
                              {v.sheetNumber && `Sheet ${v.sheetNumber} · `}
                              {v.daysLeft}d left
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                            {sel && <span style={{ color: GOLD, fontWeight: '700' }}>✓</span>}
                            <button style={{ ...S.btnGhost, padding: '3px 7px', fontSize: '10px', color: '#dc2626' }} onClick={e => { e.stopPropagation(); deleteFromVault(v.id); }}>×</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Pairing table — shown when both sides have 2+ drawings ── */}
        {(newDrawings.length > 0 && (baselineDrawings.length > 1 || selectedVaultIds.length > 1 || newDrawings.length > 1)) && (
          <div style={S.section}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              <div style={S.sectionTitle}>🔗 Sheet Pairing</div>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>Auto-matched by sheet number — override any row as needed</span>
            </div>

            <div style={{ border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 22px 1fr', background: '#f9fafb', padding: '8px 13px', borderBottom: `1px solid ${BORDER}`, fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '.05em' }}>
                <div>New Drawing</div><div /><div>Paired Baseline</div>
              </div>

              {newDrawings.map((d, i) => {
                const autoMatch   = getAutoMatch(d);
                const vaultMatch  = baselineMode === 'vault' ? vault.find(v => d.sheetNum && extractSheetNum(v.fileName) === d.sheetNum && selectedVaultIds.includes(v.id)) : null;
                const fallback    = baselineMode === 'upload' ? baselineDrawings[0] : null;
                const fallbackVault = baselineMode === 'vault' ? vault.find(v => selectedVaultIds[0] === v.id) : null;
                const resolvedFile  = autoMatch || fallback;
                const resolvedVault = vaultMatch || fallbackVault;
                const isMatched     = baselineMode === 'upload' ? !!autoMatch : !!vaultMatch;
                const bLabel        = baselineMode === 'upload'
                  ? (resolvedFile ? resolvedFile.file.name + (isMatched ? '' : ' (fallback)') : 'None assigned')
                  : (resolvedVault ? resolvedVault.fileName + (isMatched ? '' : ' (fallback)') : 'None assigned');
                const isMissing = !resolvedFile && !resolvedVault;

                return (
                  <div key={i} style={{ borderBottom: i < newDrawings.length - 1 ? `1px solid ${BORDER}` : 'none' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 22px 1fr', padding: '9px 13px', alignItems: 'center', background: i % 2 === 0 ? WHITE : '#fafafa' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                        {d.thumb ? <img src={d.thumb} alt="" style={{ width: '24px', height: '32px', objectFit: 'cover', borderRadius: '3px', border: `1px solid ${BORDER}`, flexShrink: 0 }} /> : <span style={{ fontSize: '16px' }}>📄</span>}
                        <div>
                          <div style={{ fontSize: '11px', fontWeight: '600', wordBreak: 'break-word' }}>{d.file.name.length > 20 ? d.file.name.slice(0, 17) + '…' : d.file.name}</div>
                          <div style={{ display: 'flex', gap: '3px', marginTop: '2px', flexWrap: 'wrap' }}>
                            {d.sheetNum && <span style={S.pill(GOLD)}>{d.sheetNum}</span>}
                            {isMatched  && <span style={S.pill('#22c55e')}>matched</span>}
                          </div>
                        </div>
                      </div>

                      <div style={{ textAlign: 'center', color: '#9ca3af' }}>→</div>

                      <div>
                        <div style={{ fontSize: '11px', color: isMissing ? '#dc2626' : isMatched ? '#166534' : '#6b7280', fontWeight: isMatched ? '600' : '400', marginBottom: '3px', wordBreak: 'break-word' }}>{bLabel}</div>
                        <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                          {baselineMode === 'upload'
                            ? `${baselineDrawings.length} baseline${baselineDrawings.length !== 1 ? 's' : ''} available`
                            : `${selectedVaultIds.length} vault item${selectedVaultIds.length !== 1 ? 's' : ''} selected`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Drawing Details + Save options ── */}
        <div style={S.section}>
          <div style={S.sectionTitle}>🏷️ Drawing Details (Optional)</div>
          <div>
            <label style={S.label}>Project Name</label>
            <input style={{ ...S.input, maxWidth: '360px' }} value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="e.g. 123 Main Street Office" />
          </div>
          {newDrawings.length === 1 && (
            <div style={{ marginTop: '10px' }}>
              <label style={S.label}>Sheet Number</label>
              <input style={{ ...S.input, maxWidth: '180px' }} value={newDrawings[0]?.sheetNum || ''} onChange={e => setNewDrawings(prev => prev.map((d, i) => i === 0 ? { ...d, sheetNum: e.target.value } : d))} placeholder="e.g. A-101" />
            </div>
          )}
          {newDrawings.length > 1 && (
            <div style={{ marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>Sheet numbers auto-detected per file — shown in the pairing table above.</div>
          )}
          <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input type="checkbox" id="saveBaseline" checked={saveAsBaseline} onChange={e => setSaveAsBaseline(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: GOLD }} />
            <label htmlFor="saveBaseline" style={{ fontSize: '13px', cursor: 'pointer' }}>
              Save new drawing{newDrawings.length !== 1 ? 's' : ''} to Drawing Vault as baseline{newDrawings.length !== 1 ? 's' : ''} (90 days)
            </label>
          </div>
        <div style={{ marginTop: '14px' }}>
        <label style={S.label}>Additional Focus Areas (Optional)</label>
        <input
          style={{ ...S.input, maxWidth: '480px' }}
          value={customFocus}
          onChange={e => setCustomFocus(e.target.value)}
          placeholder="e.g. panel dimensions, anchor bolt patterns, tongue and groove details"
        />
        <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '4px' }}>
          Tell the AI to pay special attention to specific elements in your drawings.
        </div>
      </div>
        </div>

        {/* Error */}
        {error && (
          <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', color: '#dc2626', fontSize: '13px' }}>
            {error}
          </div>
        )}

        {/* ── Analyze button ── */}
        <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
          <button
            style={{ ...S.btnGold, padding: '13px 36px', fontSize: '15px', opacity: analyzing ? 0.7 : 1 }}
            onClick={handleAnalyze}
            disabled={analyzing}
          >
            {analyzing
              ? `Analyzing ${analysisProgress.current} of ${analysisProgress.total}...`
              : newDrawings.length > 1
                ? `Compare ${newDrawings.length} Drawings`
                : 'Compare Drawings'}
          </button>

          {analyzing && (
            <div style={{ marginTop: '14px', maxWidth: '380px', margin: '14px auto 0' }}>
              <div style={{ background: BORDER, borderRadius: '99px', height: '8px', overflow: 'hidden', marginBottom: '8px' }}>
                <div style={{ background: GOLD, height: '100%', borderRadius: '99px', width: `${(analysisProgress.current / analysisProgress.total) * 100}%`, transition: 'width .4s ease' }} />
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>Analyzing sheet {analysisProgress.current} of {analysisProgress.total}...</div>
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '3px' }}>~20–45 seconds per sheet</div>
            </div>
          )}
        </div>

      </div>
    </>
  );
}